import * as Yup from 'yup';
import { FarmRiskType, StructureCategory, lookupsJson } from '@ourbranch/lookups';
import { getIn } from 'formik';

import {
  validHomePurchaseDate,
  requiredForRisks,
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled,
  requiredForTowers,
  requiredForGrainStorage,
  requiredForNonTowerSilos,
  requiredIfFO6SilosGrainBinsAndHeatingEnabled,
  requiredIfFO6PoultryAndHeatingEnabled,
  validYearBuilt,
  requiredWhenCanEditCoverageAmount,
  requiredWhenCanEditCoverageAmountAndFo3,
  coverageInRangeRequiredWhenEditable,
  mobileHomeSqFtValidation
} from 'common/helpers/yup-helpers';
import { brandConstants } from 'brand-config';

const primaryHeatingSourceOptions = lookupsJson.farm.primaryHeatingSourceOptions;

Yup.addMethod(Yup.mixed, 'validHomePurchaseDate', validHomePurchaseDate);
Yup.addMethod(Yup.mixed, 'requiredForRisks', requiredForRisks);
Yup.addMethod(
  Yup.mixed,
  'requiredIfFO6BarnsOutbuildingsAndHeatingEnabled',
  requiredIfFO6BarnsOutbuildingsAndHeatingEnabled
);
Yup.addMethod(Yup.mixed, 'requiredForTowers', requiredForTowers);
Yup.addMethod(Yup.mixed, 'requiredForGrainStorage', requiredForGrainStorage);
Yup.addMethod(Yup.mixed, 'requiredForNonTowerSilos', requiredForNonTowerSilos);
Yup.addMethod(Yup.mixed, 'requiredIfFO6SilosGrainBinsAndHeatingEnabled', requiredIfFO6SilosGrainBinsAndHeatingEnabled);
Yup.addMethod(Yup.mixed, 'requiredIfFO6PoultryAndHeatingEnabled', requiredIfFO6PoultryAndHeatingEnabled);
Yup.addMethod(Yup.mixed, 'requiredWhenCanEditCoverageAmount', requiredWhenCanEditCoverageAmount);
Yup.addMethod(Yup.mixed, 'requiredWhenCanEditCoverageAmountAndFo3', requiredWhenCanEditCoverageAmountAndFo3);
Yup.addMethod(Yup.mixed, 'coverageInRangeRequiredWhenEditable', coverageInRangeRequiredWhenEditable);
Yup.addMethod(Yup.string, 'validYearBuilt', validYearBuilt);
Yup.addMethod(Yup.number, 'mobileHomeSqFtValidation', mobileHomeSqFtValidation);

const baseFarmValidationSchema = Yup.object().shape({
  farmLocations: Yup.array().of(
    Yup.object().shape({
      id: Yup.string().nullable(),
      description: Yup.string().required('Description is required'),
      legalDescription: Yup.object().shape({
        format: Yup.string().required('Format is required'),
        section: Yup.string().nullable(),
        township: Yup.string().nullable(),
        townshipCoordinates: Yup.string().nullable(),
        range: Yup.string().nullable(),
        rangeCoordinates: Yup.string().nullable(),
        lot: Yup.string().nullable(),
        block: Yup.string().nullable()
      }),
      additionalLegalDescription: Yup.string().nullable(),
      locationDirections: Yup.string().nullable(),
      numberOfAcres: Yup.number().nullable(),
      address: Yup.object().shape({
        address: Yup.string().nullable(),
        address2: Yup.string().nullable(),
        country: Yup.string().nullable(),
        city: Yup.string().required('City is required'),
        state: Yup.string().required('State is required'),
        zip: Yup.string().required('Zip code is required')
      }),
      fpc: Yup.string().nullable(),
      fpcFromLocation: Yup.string().nullable(),
      fireHydrantWithin1000ft: Yup.boolean().nullable(),
      latitude: Yup.string().nullable(),
      longitude: Yup.string().nullable(),
      risks: Yup.array().of(
        Yup.object().shape({
          type: Yup.string().required('Risk type is required'),
          description: Yup.string().required('Description is required'),
          address: Yup.object().shape({
            address: Yup.string().nullable(),
            address2: Yup.string().nullable(),
            country: Yup.string().nullable(),
            city: Yup.string().nullable(),
            state: Yup.string().required('State is required'),
            zip: Yup.string().required('Zip code is required')
          }),
          building: Yup.object().shape({
            basementSqFt: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Basement sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            basementType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Basement/Foundation is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            constructionType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Construction type is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryOther
                    ],
                    propertyTypes: ['B']
                  },
                  { risk: FarmRiskType.Fo3 }
                ]
              }),
            mobileHomeQualityGrade: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['MH'] }]
              }),
            mobileHomeConfig: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Mobile home config is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['MH'] }]
              }),
            exteriorWallType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Exterior wall finish is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            garageCarCapacity: Yup.number()
              .nullable()
              .requiredForRisks({
                message: "Garage's car space is required",
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            garageType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Garage type is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            dwellingLocation: Yup.object().shape({
              address: Yup.string().nullable(),
              address2: Yup.string().nullable(),
              country: Yup.string().nullable(),
              city: Yup.string().nullable(),
              state: Yup.string().nullable(),
              zip: Yup.string().nullable()
            }),
            homeQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home quality is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numFamiliesInDwelling: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Families in dwelling is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numFireplaces: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Fireplaces is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numFullBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Full bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numHalfBathrooms: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Half bathrooms is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numOccupants: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Residents is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            numStories: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Stories is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numWoodBurningStoves: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Wood stoves is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            ownershipStatus: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Ownership status is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            purchaseDate: Yup.string().nullable(),
            purchaseAmount: Yup.number().nullable(),
            roofOriginal: Yup.boolean().nullable(),
            roofShape: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof shape is required',
                risks: [
                  { risk: FarmRiskType.Fo6Structures, structureCategories: [StructureCategory.BarnsOutbuildings] },
                  { risk: FarmRiskType.Fo3 }
                ]
              }),
            roofDeck: Yup.string().nullable(),
            roofCover: Yup.string().nullable(),
            roofDeckAttachment: Yup.string().nullable(),
            roofToWallConnection: Yup.string().nullable(),
            openingProtection: Yup.string().nullable(),
            roofSWR: Yup.string().nullable(),
            roofType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Roof type is required',
                risks: [{ risk: FarmRiskType.Fo3 }, { risk: FarmRiskType.Fo4, propertyTypes: ['MH'] }]
              }),
            roofYear: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Roof year is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            roofYearChangedManually: Yup.boolean().nullable(),
            sqFt: Yup.number()
              .nullable()
              .requiredForNonTowerSilos('Square footage is required')
              .requiredForRisks({
                message: 'Square footage is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryOther
                    ]
                  },
                  { risk: FarmRiskType.Fo3 }
                ]
              })
              .min(150, 'Square footage must be greater than 150 square feet')
              .mobileHomeSqFtValidation(),
            originalSqFt: Yup.number().nullable(),
            swimmingPool: Yup.object().shape({
              typeOfPool: Yup.string().nullable(),
              hasRemovableStepsOrLockingGate: Yup.boolean()
                .nullable()
                .when('typeOfPool', {
                  is: 'A',
                  then: Yup.boolean().nullable().required('This field is required for above-ground pools'),
                  otherwise: Yup.boolean().nullable()
                }),
              has4FtHighFenceWithSelfLatchingGate: Yup.boolean()
                .nullable()
                .when('typeOfPool', {
                  is: 'I',
                  then: Yup.boolean().nullable().required('This field is required for in-ground pools'),
                  otherwise: Yup.boolean().nullable()
                }),
              hasDivingBoardOrPoolSlide: Yup.boolean()
                .nullable()
                .when('typeOfPool', {
                  is: (value) => value === 'A' || value === 'I',
                  then: Yup.boolean().nullable().required('This field is required'),
                  otherwise: Yup.boolean().nullable()
                }),
              limit: Yup.number().nullable()
            }),
            typeOfHome: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Home type is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            occupancy: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Occupancy is required',
                risks: [{ risk: FarmRiskType.Fo6Structures }]
              }),
            yearBuilt: Yup.string()
              .nullable()
              .validYearBuilt()
              .requiredForRisks({
                message: 'Year built is required',
                risks: [{ risk: FarmRiskType.Fo6Structures }, { risk: FarmRiskType.Fo3 }]
              }),
            latitude: Yup.number().nullable(),
            longitude: Yup.number().nullable(),
            stormShutters: Yup.boolean().nullable(),
            porchSqFt: Yup.number().nullable(),
            porchCode: Yup.string().nullable(),
            constructionStyle: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Areas under construction is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            hasTrampoline: Yup.boolean().nullable(),
            // Mobile home specific fields
            tieDownType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Tie down type is required',
                risks: [
                  { risk: FarmRiskType.Fo3, propertyTypes: ['MH'] },
                  { risk: FarmRiskType.Fo4, propertyTypes: ['MH'] }
                ]
              }),
            enclosedMasonryFoundation: Yup.boolean().nullable(),
            fullSkirtingAroundBase: Yup.boolean().nullable(),
            // Fields for both Barndominium and MobileHome
            renovation: Yup.object().shape({
              electricalType: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'Electrical type is required',
                  risks: [{ risk: FarmRiskType.Fo3 }]
                }),
              electricalService: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'Electrical service is required',
                  risks: [{ risk: FarmRiskType.Fo3 }]
                }),
              heatingType: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'Heating type is required',
                  risks: [{ risk: FarmRiskType.Fo3 }]
                }),
              heatingYear: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'Heating year is required',
                  risks: [{ risk: FarmRiskType.Fo3 }]
                }),
              coolingYear: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'AC year is required',
                  risks: [{ risk: FarmRiskType.Fo3 }]
                }),
              plumbingYear: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'Plumbing year is required',
                  risks: [{ risk: FarmRiskType.Fo3 }]
                }),
              // Barndominium specific fields
              electricalYear: Yup.string()
                .nullable()
                .requiredForRisks({
                  message: 'Electrical year is required',
                  risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
                })
            }),
            attachedStructureSqFt: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Attached garage sq ft is required',
                risks: [{ risk: FarmRiskType.Fo3, propertyTypes: ['B'] }]
              }),
            numWellPumps: Yup.number().nullable(),
            numSolidFuelBurningAppliances: Yup.number()
              .nullable()
              .min(0, 'Number must be 0 or greater')
              .requiredForRisks({
                message: 'Number of appliances is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo4,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryOther
                    ]
                  },
                  { risk: FarmRiskType.Fo3, propertyTypes: ['B', 'MH'] }
                ]
              }),
            primaryHeatingSource: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Primary heating is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryOther
                    ]
                  },
                  { risk: FarmRiskType.Fo3 }
                ]
              }),
            secondaryHeatingSource: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Secondary heat is required',
                risks: [{ risk: FarmRiskType.Fo3 }]
              }),
            buildingQuality: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Quality is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins,
                      StructureCategory.PoultryBrooder,
                      StructureCategory.PoultryIncubator,
                      StructureCategory.PoultryOther
                    ]
                  }
                ]
              }),
            buildingClass: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Building class is required',
                risks: [
                  {
                    risk: FarmRiskType.Fo6Structures,
                    structureCategories: [
                      StructureCategory.BarnsOutbuildings,
                      StructureCategory.SilosGrainBins
                      // StructureCategory.PoultryBrooder,
                      // StructureCategory.PoultryIncubator,
                      // StructureCategory.PoultryOther
                    ]
                  }
                ]
              }),
            numHeatingSources: Yup.number()
              .nullable()
              .min(1, 'Heating units must be at least 1')
              .max(1000, 'Heating units cannot exceed 1000')
              .test('num-heating-sources-required', 'Heating units is required', function (value) {
                const primaryHeatingSource = getIn(this.parent, 'primaryHeatingSource');

                const heatingOption = primaryHeatingSourceOptions.find((option) => option.id === primaryHeatingSource);
                if (heatingOption?.unitCountBased) {
                  return !!value;
                }

                return true;
              }),
            numSecondaryHeatingSources: Yup.number()
              .nullable()
              .min(1, 'Heating units must be at least 1')
              .max(1000, 'Heating units cannot exceed 1000')
              .test(
                'required-if-secondary-heating-source-is-unit-count-based',
                'Heating units is required',
                function (value) {
                  const secondaryHeatingSource = getIn(this.parent, 'secondaryHeatingSource');
                  const heatingOption = primaryHeatingSourceOptions.find(
                    (option) => option.id === secondaryHeatingSource
                  );
                  if (heatingOption && heatingOption?.unitCountBased) {
                    return !!value;
                  }
                  return true;
                }
              ),
            diameter: Yup.string().nullable().requiredForTowers('Diameter is required for towers'),
            height: Yup.string()
              .nullable()
              .requiredForTowers('Height is required for towers')
              .requiredForRisks({
                message: 'Height is required',
                risks: [{ risk: FarmRiskType.Fo6Structures, structureCategories: [StructureCategory.SilosGrainBins] }]
              }),
            bushelCapacity: Yup.string()
              .nullable()
              .requiredForGrainStorage('Bushel capacity is required')
              .requiredForRisks({
                message: 'Bushel capacity is required',
                risks: [{ risk: FarmRiskType.Fo6Structures, structureCategories: [StructureCategory.SilosGrainBins] }]
              }),
            solarPanelsAttached: Yup.boolean().nullable(),
            containsInGroundPool: Yup.boolean().nullable(),
            residenceUsage: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Residence usage is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }],
                requireIfRatedOnly: true
              }),
            propertyType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Structure category is required',
                risks: [{ risk: FarmRiskType.Fo4 }, { risk: FarmRiskType.Fo3 }]
              })
          }),
          coverages: Yup.object().shape({
            coverageA: Yup.number().coverageInRangeRequiredWhenEditable([FarmRiskType.Fo3]).nullable(),
            coverageB: Yup.number().nullable(),
            coverageC: Yup.number().nullable(),
            coverageD: Yup.number().nullable(),
            coverageE: Yup.number()
              .coverageInRangeRequiredWhenEditable([FarmRiskType.Fo6Unscheduled, FarmRiskType.Fo6Scheduled])
              .nullable(),
            coverageF: Yup.array()
              .of(
                Yup.object().shape({
                  id: Yup.string().nullable(),
                  category: Yup.string().nullable(),
                  limit: Yup.number().nullable(),
                  description: Yup.string().nullable(),
                  deductible: Yup.number().nullable(),
                  deductibleWindHail: Yup.number().nullable(),
                  additionalPerils: Yup.number().nullable(),
                  multipleLossCoverage: Yup.boolean().nullable(),
                  livestockCategory: Yup.string().nullable(),
                  hasOpenPerils: Yup.boolean().nullable(),
                  hasReplacementCostCoverage: Yup.boolean().nullable(),
                  serialNumber: Yup.string().nullable(),
                  make: Yup.string().nullable(),
                  increaseStackLimit: Yup.number().nullable(),
                  hasWinterPerils: Yup.boolean().nullable(),
                  hasSpecialCattleEndorsement: Yup.boolean().nullable(),
                  hasSuffocationOfLivestock: Yup.boolean().nullable()
                })
              )
              .nullable()
              .requiredForRisks({
                message: 'At least one coverageF item is required',
                risks: [{ risk: FarmRiskType.Fo6Scheduled }]
              }),
            coverageG: Yup.number()
              .nullable()
              .requiredForRisks({
                message: 'Blanket limit is required',
                risks: [{ risk: FarmRiskType.Fo6Unscheduled }]
              }),
            coverageMS: Yup.boolean().nullable(),
            hasActiveCoverageMS: Yup.boolean().nullable(),
            deductible: Yup.number().nullable(),
            deductibleAllOther: Yup.number().nullable(),
            deductibleWindHail: Yup.number().nullable(),
            deductibleEarthquake: Yup.number().nullable(),
            deductibleEarthquake2: Yup.number().nullable(),
            roofExclusion: Yup.string().nullable(),
            roofExclusionDetails: Yup.string().nullable(),
            exteriorWallSurfacingExclusion: Yup.string().nullable(),
            exteriorWallSurfacingExclusionDetails: Yup.string().nullable(),
            otherStructuresExclusion: Yup.string().nullable(),
            otherStructuresExclusionDetails: Yup.string().nullable(),
            hasSnowWeightCoverage: Yup.boolean().nullable(),
            windHailExclusion: Yup.string().nullable(),
            windHailExclusionDetails: Yup.string().nullable(),
            otherStructuresRoofExclusion: Yup.boolean().nullable(),
            sidingExclusion: Yup.boolean().nullable(),
            sidingExclusionDetails: Yup.string().nullable(),
            stormDamageExclusion: Yup.boolean().nullable(),
            stormDamageExclusionDetails: Yup.string().nullable(),
            scheduleCoverages: Yup.array()
              .of(
                Yup.object()
                  .shape({
                    coverageType: Yup.string().nullable(),
                    items: Yup.array()
                      .of(
                        Yup.object().shape({
                          id: Yup.string().nullable(),
                          category: Yup.string().nullable(),
                          limit: Yup.number().nullable(),
                          description: Yup.string().nullable(),
                          deductible: Yup.number().nullable(),
                          extendedDetails: Yup.object()
                            .shape({
                              name: Yup.string().nullable(),
                              year: Yup.number().nullable(),
                              horsepower: Yup.number().nullable(),
                              serialNumber: Yup.string().nullable(),
                              classification: Yup.string().nullable(),
                              dimensions: Yup.string().nullable(),
                              kindOfAnimal: Yup.string().nullable(),
                              sex: Yup.string().nullable(),
                              breed: Yup.string().nullable(),
                              age: Yup.number().nullable(),
                              originalCost: Yup.number().nullable(),
                              coinsurance: Yup.number().nullable(),
                              days: Yup.number().nullable(),
                              startDate: Yup.string().nullable(),
                              endDate: Yup.string().nullable()
                            })
                            .nullable()
                        })
                      )
                      .nullable()
                  })
                  .nullable()
              )
              .nullable(),
            coverageWaterBackup: Yup.number().nullable(),
            coverageISBC: Yup.number().nullable(),
            coverageRSExtended: Yup.boolean().nullable(),
            poolLimit: Yup.number().nullable(),
            resUnderConstructionLimit: Yup.number().nullable(),
            resUnderConstructionEffectiveDate: Yup.string().nullable(),
            inlandFloodInceptionDate: Yup.string().nullable(),
            inlandFloodOccurrenceLimit: Yup.number().nullable(),
            inlandFloodAggregateLimit: Yup.number().nullable(),
            inlandFloodDeductible: Yup.number().nullable(),
            inlandFloodDeductibleOverride: Yup.number().nullable(),
            inlandFloodRiskScore: Yup.number().nullable(),
            expandedDebrisRemoval: Yup.boolean().nullable(),
            serviceLine: Yup.boolean().nullable(),
            personalPropertyReplCost: Yup.boolean().nullable(),
            businessPropertyLimit: Yup.number().nullable(),
            businessPropertyLimitDetails: Yup.string().nullable(),
            moneyLimit: Yup.number().nullable(),
            jewelryLimit: Yup.number().nullable(),
            securitiesLimit: Yup.number().nullable(),
            motorizedVehiclesLimit: Yup.number().nullable(),
            silverwareLimit: Yup.number().nullable(),
            gunsLimit: Yup.number().nullable(),
            tenantImprovementLimit: Yup.number().nullable(),
            sprinklerLeakageBuildingLimit: Yup.number().nullable(),
            sprinklerLeakageLimitDetails: Yup.string()
              .nullable()
              .when('sprinklerLeakageBuildingLimit', {
                is: (value) => value != null && value > 0,
                then: Yup.string().required(
                  'Description of premise is required when sprinkler leakage limit is provided'
                ),
                otherwise: Yup.string().nullable()
              }),
            canEditCoverageAmount: Yup.boolean().nullable(),
            adjustDwellingLimitDescription: Yup.string()
              .requiredWhenCanEditCoverageAmount('Description of reason for adjusting dwelling limit is required')
              .nullable(),
            isRCEFromExternalSource: Yup.string()
              .requiredWhenCanEditCoverageAmountAndFo3('Selecting yes or no is required')
              .nullable(),
            cosmeticDamageExclusion: Yup.boolean()
              .nullable()
              .test(
                'at-least-one-exclusion-type',
                'You must have at least one cosmetic damage surfacing type selected in order to exclude it.',
                function (value) {
                  if (!value) {
                    return true;
                  }

                  const { exteriorDoorsWindowsDamageExclusion, exteriorWallsDamageExclusion, roofsDamageExclusion } =
                    this.parent;

                  return exteriorDoorsWindowsDamageExclusion || exteriorWallsDamageExclusion || roofsDamageExclusion;
                }
              ),
            exteriorDoorsWindowsDamageExclusion: Yup.boolean().nullable(),
            exteriorWallsDamageExclusion: Yup.boolean().nullable(),
            roofsDamageExclusion: Yup.boolean().nullable()
          }),
          connectedHome: Yup.object().shape({
            monitored: Yup.string().nullable(),
            moistureDevices: Yup.number().nullable(),
            theftPreventionDevices: Yup.number().nullable(),
            theftPreventionDeviceType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Theft protective device is required',
                risks: [{ risk: FarmRiskType.Fo3 }, { risk: FarmRiskType.Fo4 }]
              }),
            motionDetectingDevices: Yup.number().nullable(),
            smokeDetectingDevices: Yup.number().nullable(),
            fireProtectiveDeviceType: Yup.string()
              .nullable()
              .requiredForRisks({
                message: 'Fire protective device is required',
                risks: [{ risk: FarmRiskType.Fo3 }, { risk: FarmRiskType.Fo4 }]
              }),
            internetMonitoring: Yup.boolean().nullable(),
            autoWaterShutoffDevices: Yup.boolean().nullable(),
            providerName: Yup.string().nullable(),
            agreeToTerms: Yup.boolean().nullable()
          })
        })
      )
    })
  )
});

export const getSharedFarmSchema = async () => {
  let schema = baseFarmValidationSchema;

  try {
    const brandValidationModule = await import(`./brand-schemas/${brandConstants.SHORT_NAME?.toLowerCase()}.js`);

    if (brandValidationModule.default) {
      schema = schema.concat(brandValidationModule.default);
    }
  } catch (error) {
    console.warn(`Brand validation schema not found for ${brandConstants.SHORT_NAME?.toLowerCase()}`);
  }

  return schema;
};
